'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Loader2 } from 'lucide-react';
import { WebOnlyBillingAction } from '@/components/billing/web-only-billing-action';
import { createClient } from '@/lib/supabase/client';
import type { BillingInterval, PaidBillingPlan } from '@/lib/api';

export function billingCheckoutPath(
  plan: PaidBillingPlan,
  interval: BillingInterval = 'month',
): string {
  const params = new URLSearchParams({ checkout: 'start', plan, interval });
  return `/dashboard/settings/billing?${params.toString()}`;
}

export function pricingCheckoutDestination(
  signedIn: boolean,
  plan: PaidBillingPlan,
  interval: BillingInterval = 'month',
): string {
  const checkoutPath = billingCheckoutPath(plan, interval);
  return signedIn
    ? checkoutPath
    : `/signup?returnTo=${encodeURIComponent(checkoutPath)}`;
}

export function PricingCheckoutCta({
  plan,
  label,
  featured = false,
  interval = 'month',
  className = '',
}: {
  plan: PaidBillingPlan;
  label: string;
  featured?: boolean;
  interval?: BillingInterval;
  className?: string;
}) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const continueToCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await createClient().auth.getSession();
      router.push(pricingCheckoutDestination(Boolean(data.session), plan, interval));
    } catch {
      router.push(pricingCheckoutDestination(false, plan, interval));
    }
  };

  return (
    <WebOnlyBillingAction>
      <button
        type="button"
        onClick={() => void continueToCheckout()}
        disabled={loading}
        className={`mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-center text-sm font-bold transition-transform hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70 ${
          featured ? 'bg-[#181611] text-white' : 'border border-black/15 bg-white hover:bg-[#fbfaf7]'
        } ${className}`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? 'Opening checkout…' : label}
        {featured && !loading ? <ArrowRight className="h-4 w-4" /> : null}
      </button>
    </WebOnlyBillingAction>
  );
}
